import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Wrench, CheckCircle, Info, Activity, Settings as SettingsIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useFacility } from '../context/FacilityContext';
import intelligenceService from '../services/intelligenceService';
import VoiceAgentKeyCard from '../components/aiva/VoiceAgentKeyCard';

/**
 * AIVA / Intelligence page. Two sub-tabs:
 *   /intelligence                  -> Overview (alerts + predictive maintenance)
 *   /intelligence?view=settings    -> AIVA Settings (Vapi X-API-Key mint/revoke)
 *
 * The Settings tab houses AIVA-specific config. The voice-agent API key
 * card lived in Communication > Settings until 2026-06-01 when it was
 * moved here because it's AIVA config, not comms-channel config.
 */

const TABS = [
  { key: 'overview', label: 'Overview', icon: Activity },
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

const Intelligence = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  const activeKey = rawView && TABS.find((t) => t.key === rawView) ? rawView : 'overview';

  const setTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'overview') params.delete('view');
    else params.set('view', key);
    setSearchParams(params, { replace: true });
  };

  const [alerts, setAlerts] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acknowledged, setAcknowledged] = useState(new Set());

  const load = useCallback(async () => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        intelligenceService.listAlerts(facilityId),
        intelligenceService.maintenanceForecast(facilityId),
      ]);
      setAlerts(a.data || []);
      setMaintenance(m.data || []);
    } catch (err) {
      console.error('Failed to load intelligence data:', err);
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading]);

  useEffect(() => {
    if (activeKey === 'overview') load();
  }, [load, activeKey]);

  const handleAck = async (alert) => {
    try {
      await intelligenceService.acknowledgeAlert(facilityId, { kind: alert.kind });
      setAcknowledged((prev) => new Set([...prev, alert.kind]));
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  if (facilityLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
        <p className="text-[13px] text-slate-500 mt-1">Create a facility in Settings to see intelligence insights.</p>
      </div>
    );
  }

  const visibleAlerts = alerts.filter((a) => !acknowledged.has(a.kind));

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900">AI</h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          {activeKey === 'settings'
            ? 'AIVA configuration for this facility.'
            : 'Anomaly alerts and predictive maintenance for this facility.'}
        </p>
      </div>

      <div className="border-b border-slate-200 mb-5">
        <nav className="-mb-px flex gap-6" aria-label="AIVA tabs">
          {TABS.map((t) => {
            const active = activeKey === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-medium transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeKey === 'settings' ? (
        <VoiceAgentKeyCard facility={currentFacility} />
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <section className="mb-6">
            <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Active Alerts
            </h2>
            {visibleAlerts.length === 0 ? (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-5 py-4 flex items-center gap-3">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <p className="text-[13px] text-emerald-700">No anomalies detected. Facility operating in normal bands.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {visibleAlerts.map((alert) => (
                  <AlertCard key={alert.kind} alert={alert} onAck={() => handleAck(alert)} />
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Predictive Maintenance
            </h2>
            <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
              {maintenance.length === 0 ? (
                <p className="px-5 py-6 text-center text-[13px] text-slate-400">
                  No units flagged for maintenance.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">Unit</th>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">Why</th>
                      <th className="px-5 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maintenance.map((m) => (
                      <tr key={m.unit_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                        <td className="px-5 py-2.5">
                          <p className="font-medium text-slate-800">{m.unit_number}</p>
                          <p className="text-[11px] text-slate-400">{m.unit_type}</p>
                        </td>
                        <td className="px-5 py-2.5 text-[12px] text-slate-500">
                          {m.reasons.join(' · ')}
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-bold ${
                            m.score >= 70 ? 'bg-red-50 text-red-700' :
                            m.score >= 50 ? 'bg-amber-50 text-amber-700' :
                            'bg-slate-50 text-slate-500'
                          }`}>
                            {m.score}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

const AlertCard = ({ alert, onAck }) => {
  const colors = {
    high: { border: 'border-red-200', bg: 'bg-red-50', icon: 'text-red-600', text: 'text-red-700' },
    medium: { border: 'border-amber-200', bg: 'bg-amber-50', icon: 'text-amber-600', text: 'text-amber-700' },
    low: { border: 'border-slate-200', bg: 'bg-slate-50', icon: 'text-slate-500', text: 'text-slate-600' },
  };
  const c = colors[alert.severity] || colors.low;
  const Icon = alert.severity === 'high' ? AlertTriangle : alert.severity === 'medium' ? Info : Wrench;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} px-5 py-3 flex items-start gap-3`}>
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${c.icon}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[13px] font-semibold ${c.text}`}>{alert.title}</p>
        <p className={`text-[12px] ${c.text} opacity-80`}>{alert.detail}</p>
      </div>
      <button
        onClick={onAck}
        className={`text-[11px] font-medium ${c.text} hover:underline`}
      >
        Acknowledge
      </button>
    </div>
  );
};

export default Intelligence;
